import { useEffect, useReducer, useRef, useCallback } from "react";

export enum statusTypes {
  Initial = "Initial",
  Pending = "Pending",
  Fulfilled = "Fulfilled",
  Rejected = "Rejected",
}

enum actionTypes {
  Init = "Init",
  Run = "Run",
  Abort = "Abort",
  Fulfill = "Fulfill",
  Reject = "Reject",
}

interface Args {
  type: actionTypes;
  payload: any;
}

const getStatusProps = (status: statusTypes) => ({
  status,
  isInitial: status === statusTypes.Initial,
  isPending: status === statusTypes.Pending,
  isFulfilled: status === statusTypes.Fulfilled,
  isRejected: status === statusTypes.Rejected,
});

const initialize = () => ({
  data: undefined,
  error: undefined,
  ...getStatusProps(statusTypes.Initial),
});

const reducer = (state: any, args: Args) => {
  const { type, payload } = args;
  switch (type) {
    case actionTypes.Init:
      return initialize();
    case actionTypes.Run:
      return {
        ...state,
        data: undefined,
        error: undefined,
        ...getStatusProps(statusTypes.Pending),
      };
    case actionTypes.Fulfill:
      return {
        ...state,
        data: payload,
        error: undefined,
        ...getStatusProps(statusTypes.Fulfilled),
      };
    case actionTypes.Reject:
      return {
        ...state,
        data: undefined,
        error: payload,
        ...getStatusProps(statusTypes.Rejected),
      };
    case actionTypes.Abort:
      return {
        data: undefined,
        error: payload,
        ...getStatusProps(statusTypes.Rejected),
      };
  }
};

export interface UseAsyncFn {
  (...args: any[]): any;
}

export const useAsyncFn: UseAsyncFn = ({ promiseFn, deferFn, ...args }) => {
  const [state, dispatch] = useReducer(reducer, initialize());
  const asyncFn = promiseFn || deferFn;
  // Keep a ref so run() always calls the latest function without needing to
  // be recreated whenever the caller passes a new (but functionally identical)
  // function reference. Without this, an unstable deferFn would cause the
  // useEffect cleanup to fire abort() on every parent re-render, cancelling
  // in-flight requests before they could fulfill.
  const asyncFnRef = useRef(asyncFn);
  asyncFnRef.current = asyncFn;
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController>();
  const argsRef = useRef(args);
  // Monotonic id so a superseded run never overwrites shared state (e.g. fast filter switches).
  // We intentionally do NOT abort the previous request on a new run() — that would change
  // behavior for mutations (PUT/POST/DELETE) across the app. Stale responses are ignored below.
  const runIdRef = useRef(0);
  const init = (): void => {
    isMountedRef.current &&
      dispatch({ type: actionTypes.Init, payload: undefined });
  };

  const run = useCallback(
    async (newArgs?: any) => {
      if (newArgs) {
        argsRef.current = newArgs;
      }

      const runId = ++runIdRef.current;
      abortControllerRef.current = new AbortController();

      isMountedRef.current &&
        dispatch({ type: actionTypes.Run, payload: undefined });
      try {
        const signal: AbortSignal | undefined =
          abortControllerRef.current && abortControllerRef.current.signal;
        const payload = await asyncFnRef.current({ args: argsRef.current, signal });
        // Always return payload to this run's awaiter; only skip shared-state update if stale
        if (runId === runIdRef.current && isMountedRef.current) {
          dispatch({ type: actionTypes.Fulfill, payload });
        }
        return payload;
      } catch (error) {
        const isAbort = error && error.name === "AbortError";
        // Superseded run: don't touch shared state (would clobber the newer run)
        if (runId !== runIdRef.current) {
          if (!isAbort) throw error;
          return;
        }
        const curType = isAbort ? actionTypes.Abort : actionTypes.Reject;
        isMountedRef.current && dispatch({ type: curType, payload: error });
        if (!isAbort) {
          throw error;
        }
      }
    },
    [] // stable — uses asyncFnRef.current so no stale-closure issue
  );

  const abort = (): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );
  useEffect(() => {
    if (promiseFn) {
      run();
    }
    return abort;
  }, [promiseFn, run]);
  return { ...state, run, init, abort };
};
